import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Calendar,
  MapPin,
  Users,
  MoreVertical,
  Trash2,
  Edit,
  Eye,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  const [newEvent, setNewEvent] = useState({
    event_name: '',
    event_location: '',
    event_description: '',
    expected_attendees: 100,
    start_date: '',
    end_date: '',
    branding_color: '#3B82F6'
  });

  const fetchEvents = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await api.get('/events', { params });
      setEvents(response.data || []);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const eventData = {
        event_name: newEvent.event_name,
        event_location: newEvent.event_location,
        event_description: newEvent.event_description,
        expected_attendees: parseInt(newEvent.expected_attendees),
        event_dates: [{
          start_date: newEvent.start_date,
          end_date: newEvent.end_date || newEvent.start_date
        }],
        branding_color: newEvent.branding_color
      };
      
      const response = await api.post('/events', eventData);
      toast.success('Event created successfully!');
      setCreateDialogOpen(false);
      setNewEvent({
        event_name: '',
        event_location: '',
        event_description: '',
        expected_attendees: 100,
        start_date: '',
        end_date: '',
        branding_color: '#3B82F6'
      });
      navigate(`/admin/events/${response.data.event.event_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete "${eventName}"? This will delete all attendees, scan logs, and related data.`)) {
      return;
    }
    
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout title="Events">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="text-sm text-gray-500">
          {events.length} event{events.length !== 1 ? 's' : ''}
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-event-button">
              <Plus className="h-4 w-4 mr-2" /> Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
              <DialogDescription>Fill in the details to create a new event</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="event_name">Event Name *</Label>
                <Input
                  id="event_name"
                  value={newEvent.event_name}
                  onChange={(e) => setNewEvent({...newEvent, event_name: e.target.value})}
                  placeholder="Tech Conference 2025"
                  required
                  data-testid="event-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="event_location">Location *</Label>
                <Input
                  id="event_location"
                  value={newEvent.event_location}
                  onChange={(e) => setNewEvent({...newEvent, event_location: e.target.value})}
                  placeholder="Convention Center, Mumbai"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={newEvent.start_date}
                    onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={newEvent.end_date}
                    onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expected_attendees">Expected Attendees</Label>
                  <Input
                    id="expected_attendees"
                    type="number"
                    value={newEvent.expected_attendees}
                    onChange={(e) => setNewEvent({...newEvent, expected_attendees: e.target.value})}
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branding_color">Brand Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="branding_color"
                      type="color"
                      value={newEvent.branding_color}
                      onChange={(e) => setNewEvent({...newEvent, branding_color: e.target.value})}
                      className="w-14 h-10 p-1"
                    />
                    <Input
                      value={newEvent.branding_color}
                      onChange={(e) => setNewEvent({...newEvent, branding_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="event_description">Description</Label>
                <Textarea
                  id="event_description"
                  value={newEvent.event_description}
                  onChange={(e) => setNewEvent({...newEvent, event_description: e.target.value})}
                  placeholder="Brief description of the event..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} data-testid="submit-event-button">
                  {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</> : 'Create Event'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Events Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No events yet</h3>
            <p className="text-gray-500 mb-4">Create your first event to get started</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <Card key={event.event_id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`event-card-${event.event_id}`}>
              <div
                className="h-2"
                style={{ backgroundColor: event.branding_color || '#3B82F6' }}
              />
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-lg line-clamp-1">{event.event_name}</h3>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/events/${event.event_id}`)}>
                        <Eye className="h-4 w-4 mr-2" /> View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/admin/events/${event.event_id}`)}>
                        <Edit className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteEvent(event.event_id, event.event_name)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span className="line-clamp-1">{event.event_location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {event.event_dates?.[0]?.start_date 
                        ? formatDate(event.event_dates[0].start_date)
                        : 'TBA'
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>{event.actual_attendees || 0} / {event.expected_attendees || 0} attendees</span>
                  </div>
                </div>
                
                <Link to={`/admin/events/${event.event_id}`}>
                  <Button variant="outline" className="w-full" data-testid={`view-event-${event.event_id}`}>
                    Manage Event
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default EventsList;
